import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkDeleteSiteResponse,
  DeployStandalonePagesApplicationErrors,
  DuplicateSiteOptions,
  DuplicateSiteResponse,
  PublishSiteApplicationErrors,
  bulkDeleteSite as universalBulkDeleteSite,
  deployStandalonePages as universalDeployStandalonePages,
  duplicateSite as universalDuplicateSite,
  publishSite as universalPublishSite,
} from './site-actions-v1-site-actions-site-actions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/sites' };

export function publishSite(httpClient: HttpClient): PublishSiteSignature {
  return () =>
    universalPublishSite(
      // @ts-ignore
      { httpClient }
    );
}

interface PublishSiteSignature {
  /**
   * Publishes a site.
   *
   * Publishing a site makes any changes previously saved on the site available on the internet.
   * After publishing, changes to your site appear in the [site's history](https://support.wix.com/en/article/viewing-and-managing-your-site-history).
   *
   * When using an API key for authentication, include a `wix-site-id` header with the ID of the site you want to publish.
   */
  (): Promise<
    void & {
      __applicationErrorsType?: PublishSiteApplicationErrors;
    }
  >;
}

export function deployStandalonePages(
  httpClient: HttpClient
): DeployStandalonePagesSignature {
  return () =>
    universalDeployStandalonePages(
      // @ts-ignore
      { httpClient }
    );
}

interface DeployStandalonePagesSignature {
  /**
   * Publishes a headless site.
   *
   * Publishing only the headless pages of the site.
   *
   * When using an API key for authentication, include a `wix-site-id` header with the ID of the site you want to publish.
   */
  (): Promise<
    void & {
      __applicationErrorsType?: DeployStandalonePagesApplicationErrors;
    }
  >;
}

export function duplicateSite(httpClient: HttpClient): DuplicateSiteSignature {
  return (
    sourceSiteId: string,
    options: NonNullablePaths<DuplicateSiteOptions, `siteDisplayName`, 0>
  ) =>
    universalDuplicateSite(
      sourceSiteId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DuplicateSiteSignature {
  /**
   * Duplicates a site with a new site name.
   *
   * > **Note:** When you duplicate a site, some business-related content such as store orders, contacts, invoices, and 3rd-party app settings are not be included.
   * > The duplicated site won’t have a domain or any Premium capabilities.
   *
   * Any installed apps that can be used only on sites with a Premium Plan, will be copied to the duplicated site and
   * will appear unactivated. Once the site is upgraded, the app will be available for use.
   *
   * > **Important:** This call requires an account level API key.
   * @param - ID of the site to duplicated.
   */
  (
    sourceSiteId: string,
    options: NonNullablePaths<DuplicateSiteOptions, `siteDisplayName`, 0>
  ): Promise<NonNullablePaths<DuplicateSiteResponse, `newSiteId`, 0>>;
}

export function bulkDeleteSite(
  httpClient: HttpClient
): BulkDeleteSiteSignature {
  return (ids: string[]) =>
    universalBulkDeleteSite(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteSiteSignature {
  /**
   * This endpoint enables you to delete multiple sites.
   *
   * This is not a permanent delete. Sites are moved to the trash bin and can be restored through site collaborators.
   *
   * Learn more about [deleting multiple sites](https://support.wix.com/en/article/moving-a-site-to-trash).
   *
   * > **Important:** This call requires an account level API key.
   * @param - Site IDs.
   *
   * Min: 1 site ID <br>
   * Max: 20 site IDs
   */
  (ids: string[]): Promise<
    NonNullablePaths<
      BulkDeleteSiteResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`,
      4
    >
  >;
}

export {
  ApplicationError,
  BulkActionMetadata,
  BulkDeleteSiteRequest,
  BulkDeleteSiteResponse,
  BulkSiteResult,
  CreateAndAttachChildRequest,
  CreateAndAttachChildResponse,
  DeleteSiteFromTrashRequest,
  DeleteSiteFromTrashResponse,
  DeployStandalonePagesRequest,
  DeployStandalonePagesResponse,
  DuplicateSiteOptions,
  DuplicateSiteRequest,
  DuplicateSiteResponse,
  EmptyEntity,
  ItemMetadata,
  MoveSiteToTrashRequest,
  MoveSiteToTrashResponse,
  PublishSiteRequest,
  PublishSiteResponse,
  RenameSiteRequest,
  RenameSiteResponse,
  RestoreSiteFromTrashRequest,
  RestoreSiteFromTrashResponse,
  UnpublishSiteRequest,
  UnpublishSiteResponse,
  ValidateSiteNameRequest,
  ValidateSiteNameResponse,
} from './site-actions-v1-site-actions-site-actions.universal.js';
